import { useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Button, Alert, Badge } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { CopyChip } from '../../components/CopyChip';
import { StatusBadge } from '../../components/StatusBadge';
import { RelativeTime } from '../../components/RelativeTime';
import { useAuth } from '../../auth/AuthProvider';
import { useBrand } from '../../brand/BrandProvider';
import { useMe } from '../../me/MeProvider';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode } from '../../api/fixtures';

/**
 * Diagnose-Page für Live-API-Verifikation.
 *
 * Zeigt alle State-Variablen die in der Operator-Bootstrap-Kette vorkommen
 * + bietet Ad-hoc-Probes für die kritischen Endpoints (`/healthz`, `/v1/auth/refresh`,
 * `/v1/operator/brands`, `/v1/operator/me`).
 *
 * Route: /admin/diagnostics — sichtbar mit any-auth-permission
 */
export function DiagnosticsPage() {
  const auth = useAuth();
  const brand = useBrand();
  const me = useMe();
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  const [probes, setProbes] = useState({});

  const probe = (label, fn) => async () => {
    setProbes((p) => ({ ...p, [label]: { status: 'pending', startedAt: Date.now() } }));
    try {
      const res = await fn();
      setProbes((p) => ({
        ...p,
        [label]: {
          status: 'ok',
          response: res,
          finishedAt: Date.now(),
        },
      }));
    } catch (err) {
      setProbes((p) => ({
        ...p,
        [label]: {
          status: 'error',
          error: {
            code: err?.code ?? null,
            message: err?.message ?? String(err),
            status: err?.status ?? null,
          },
          finishedAt: Date.now(),
        },
      }));
    }
  };

  return (
    <div>
      <PageHeader
        title="Diagnostics"
        subtitle="Verifikation des Bootstrap-Flows + Ad-hoc-Probes gegen Core API"
      />

      {fixturesMode && (
        <Alert color="warning">
          <strong>VITE_USE_FIXTURES=1 ist aktiv.</strong> Alle Probes laufen
          gegen die Mock-Layer, NICHT gegen die echte Core API. Setze
          <code>VITE_USE_FIXTURES=</code> in <code>.env.local</code> und reload,
          um echt zu testen.
        </Alert>
      )}

      <Row className="g-3 mb-3">
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Auth</CardTitle>
              <dl className="row small mb-0">
                <Detail label="Status" value={<StatusBadge value={auth.status === 'authenticated' ? 'active' : auth.status} />} />
                <Detail label="Access-Token" value={auth.getAccessToken() ? `••••${auth.getAccessToken().slice(-8)}` : 'nicht gesetzt'} />
                <Detail label="Refresh-Token" value={auth.getRefreshToken() ? `••••${auth.getRefreshToken().slice(-8)}` : 'nicht gesetzt'} />
                <Detail label="MFA Challenge" value={auth.mfaChallenge ? 'pending' : '—'} />
                <Detail label="Fixture-Mode" value={fixturesMode ? <Badge color="warning">aktiv</Badge> : <Badge color="success">aus (Live-API)</Badge>} />
              </dl>
            </CardBody>
          </Card>
        </Col>

        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h5">Brand-Context</CardTitle>
              <dl className="row small mb-0">
                <Detail label="Status" value={<StatusBadge value={brand.status === 'ready' ? 'ready' : brand.status} />} />
                <Detail label="brands[]" value={`${brand.brands.length} verfügbar`} />
                <Detail label="currentBrandOrigin" value={brand.currentBrandOrigin ? <CopyChip value={brand.currentBrandOrigin} /> : '—'} />
                <Detail label="currentBrand.slug" value={brand.currentBrand?.slug ?? '—'} />
                <Detail label="currentBrand.id" value={brand.currentBrand?.id ?? '—'} />
              </dl>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col>
          <Card>
            <CardBody>
              <CardTitle tag="h5">/v1/operator/me</CardTitle>
              <dl className="row small mb-2">
                <Detail label="status" value={<StatusBadge value={me.status === 'ready' ? 'ready' : me.status} />} />
                <Detail label="user.id" value={me.me?.user?.id ?? '—'} />
                <Detail label="user.email" value={me.me?.user?.email ?? '—'} />
                <Detail label="user.role" value={me.me?.user?.role ? <StatusBadge value={me.me.user.role} /> : '—'} />
                <Detail label="currentBrand.brandOrigin" value={me.me?.currentBrand?.brandOrigin ?? '—'} />
              </dl>
              <div className="mt-3">
                <strong className="small text-uppercase text-muted">Permissions ({me.permissions.length})</strong>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {me.permissions.length === 0 && <span className="text-muted small">leer</span>}
                  {me.permissions.map((p) => (
                    <Badge
                      key={p}
                      color="secondary"
                      pill
                      className="orbit-code-badge"
                    >
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <CardTitle tag="h5">Endpoint-Probes</CardTitle>
          <p className="text-muted small">
            Manuelle Aufrufe gegen Core. Auth-Header und Brand-Origin werden
            vom ApiClient automatisch gesetzt — Browser-DevTools-Network
            zeigt die echten Requests.
          </p>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button
              size="sm"
              color="primary"
              outline
              onClick={probe('GET /v1/operator/brands', () => fetch('/v1/operator/brands'))}
            >
              GET /v1/operator/brands
            </Button>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={probe('GET /v1/operator/me', () => fetch('/v1/operator/me'))}
            >
              GET /v1/operator/me
            </Button>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={probe('GET /v1/admin/users (limit=1)', () => fetch('/v1/admin/users?q=__none__'))}
            >
              GET /v1/admin/users
            </Button>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={probe('GET /v1/admin/audit-log', () => fetch('/v1/admin/audit-log'))}
            >
              GET /v1/admin/audit-log
            </Button>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={probe('GET /v1/admin/api-keys', () => fetch('/v1/admin/api-keys'))}
            >
              GET /v1/admin/api-keys
            </Button>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={probe('GET /v1/admin/settings', () => fetch('/v1/admin/settings'))}
            >
              GET /v1/admin/settings
            </Button>
          </div>

          <div>
            {Object.entries(probes).map(([label, p]) => (
              <ProbeResult key={label} label={label} probe={p} />
            ))}
            {Object.keys(probes).length === 0 && (
              <div className="text-muted small">Noch keine Probes ausgeführt.</div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function ProbeResult({ label, probe }) {
  const elapsed = probe.finishedAt ? `${probe.finishedAt - probe.startedAt}ms` : '…';
  const color = probe.status === 'ok' ? 'success' : probe.status === 'error' ? 'danger' : 'secondary';
  return (
    <div className="mb-3 p-2 rounded" style={{ background: 'var(--bs-tertiary-bg)' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <code style={{ fontSize: 13 }}>{label}</code>
        <div className="d-flex gap-2 align-items-center small">
          <Badge color={color}>{probe.status}</Badge>
          <span className="text-muted">{elapsed}</span>
          <RelativeTime iso={new Date(probe.finishedAt ?? probe.startedAt).toISOString()} />
        </div>
      </div>
      {probe.error && (
        <Alert color="danger" className="small mb-0 py-2">
          <strong>code:</strong> <code>{probe.error.code ?? 'unknown'}</code>
          {' · '}
          <strong>HTTP:</strong> {probe.error.status ?? '—'}
          <br />
          {probe.error.message}
        </Alert>
      )}
      {probe.response && (
        <pre
          className="p-2 mb-0"
          style={{
            background: 'var(--bs-body-bg)',
            fontFamily: 'ui-monospace, monospace',
            fontSize: 11,
            maxHeight: 240,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {JSON.stringify(probe.response, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <>
      <dt className="col-sm-5 text-muted text-uppercase">{label}</dt>
      <dd className="col-sm-7 fw-semibold">{value ?? '—'}</dd>
    </>
  );
}
